import apiClient, { handleApiError } from './apiClient';

export interface Categoria {
  id: number;
  nombre: string;
  descripcion: string;
  activo: boolean;
}

export interface Proveedor {
  id: number;
  nombre: string;
  ruc: string;
  contacto: string;
  telefono: string;
  email: string;
  direccion: string;
  activo: boolean;
}

export interface Producto {
  id: number;
  codigo: string;
  nombre: string;
  descripcion: string;
  categoria: number;
  categoria_nombre?: string;
  proveedor: number;
  proveedor_nombre?: string;
  precio_compra: number;
  precio_venta: number;
  unidad_medida: 'unidad' | 'kg' | 'litro' | 'paquete' | 'caja';
  stock_minimo: number;
  imagen: string | null;
  activo: boolean;
  // Campos calculados
  margen_ganancia?: number;
  stock_actual?: number;
  necesita_reposicion?: boolean;
}

export interface ProductoCreate {
  codigo: string;
  nombre: string;
  descripcion: string;
  categoria: number;
  proveedor: number;
  precio_compra: number;
  precio_venta: number;
  unidad_medida: 'unidad' | 'kg' | 'litro' | 'paquete' | 'caja';
  stock_minimo: number;
  imagen?: File | null;
}

class ProductosService {
  // Productos
  async getProductos(params?: {
    activo?: boolean;
    categoria?: number;
    search?: string;
  }): Promise<Producto[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.activo !== undefined) queryParams.append('activo', String(params.activo));
      if (params?.categoria) queryParams.append('categoria', String(params.categoria));
      if (params?.search) queryParams.append('search', params.search);
      
      const url = `/productos/productos/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get<Producto[]>(url);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getProducto(id: number): Promise<Producto> {
    try {
      const response = await apiClient.get<Producto>(`/productos/productos/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createProducto(data: ProductoCreate): Promise<Producto> {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const response = await apiClient.post<Producto>('/productos/productos/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateProducto(id: number, data: Partial<ProductoCreate>): Promise<Producto> {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const response = await apiClient.put<Producto>(`/productos/productos/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async deleteProducto(id: number): Promise<void> {
    try {
      await apiClient.delete(`/productos/productos/${id}/`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Categorías
  async getCategorias(activo?: boolean): Promise<Categoria[]> {
    try {
      const url = activo !== undefined 
        ? `/productos/categorias/?activo=${activo}` 
        : '/productos/categorias/';
      const response = await apiClient.get<Categoria[]>(url);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getCategoria(id: number): Promise<Categoria> {
    try {
      const response = await apiClient.get<Categoria>(`/productos/categorias/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createCategoria(data: Omit<Categoria, 'id'>): Promise<Categoria> {
    try {
      const response = await apiClient.post<Categoria>('/productos/categorias/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateCategoria(id: number, data: Partial<Categoria>): Promise<Categoria> {
    try {
      const response = await apiClient.put<Categoria>(`/productos/categorias/${id}/`, data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async deleteCategoria(id: number): Promise<void> {
    try {
      await apiClient.delete(`/productos/categorias/${id}/`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Proveedores
  async getProveedores(activo?: boolean): Promise<Proveedor[]> {
    try {
      const url = activo !== undefined 
        ? `/productos/proveedores/?activo=${activo}` 
        : '/productos/proveedores/';
      const response = await apiClient.get<Proveedor[]>(url);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getProveedor(id: number): Promise<Proveedor> {
    try {
      const response = await apiClient.get<Proveedor>(`/productos/proveedores/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createProveedor(data: Omit<Proveedor, 'id'>): Promise<Proveedor> {
    try {
      const response = await apiClient.post<Proveedor>('/productos/proveedores/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateProveedor(id: number, data: Partial<Proveedor>): Promise<Proveedor> {
    try {
      const response = await apiClient.put<Proveedor>(`/productos/proveedores/${id}/`, data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async deleteProveedor(id: number): Promise<void> {
    try {
      await apiClient.delete(`/productos/proveedores/${id}/`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

const productosService = new ProductosService();
export { productosService };
export default productosService;
